/**
 * @mixin submitFormMixin
 * @author pierre-michel@on-running.com
 *
 * Standardize the way we display errors coming from the server (global error message below the
 * submit button "Please correct the fields marked in red." and specific error messages below the
 * targeted fields. Often comes together with validateForm mixin
 *
 * NOTE: You don't have to use this mixin to submit a form if it is not standard and
 * should handle errors in a different way.
 */

export default {
  data() {
    return {
      errorCount: 0,
      isSubmitting: false,
      isRedirecting: false,
      hasSubmitError: false,
      submittedSuccessfully: false,
      errorMessage: undefined,
      saveButtonTimeout: On.TransitionTimes.medium + 1000,
      minLoadingStateTime: 500, // spinner should be displayed for at least...
    }
  },

  computed: {
    formStatus() {
      return {
        stopEditingHandler: this.stopEditingHandler,
        errorCount: this.errorCount,
        isSubmitting: this.isSubmitting,
        hasSubmitError: this.hasSubmitError,
        submittedSuccessfully: this.submittedSuccessfully,
        successMessage: this.successMessage,
      }
    },
  },

  methods: {
    handleAxiosEvent(handler) {
      const timeNow = new Date().getTime()
      const httpRequestExecutionTime = timeNow - this.httpRequestStartedAt
      const loadingStateRemainingTime = this.minLoadingStateTime - httpRequestExecutionTime

      loadingStateRemainingTime > 0 ? setTimeout(handler, loadingStateRemainingTime) : handler()
    },

    submitForm(axiosPromise, customEvents = {}) {
      const defaultCustomEvents = {
        before: () => {},
        then: () => {},
        catch: () => {},
        finally: () => {},
      }
      const customEventsFormatted = Object.assign({}, defaultCustomEvents, customEvents)
      customEventsFormatted.before()
      this.isSubmitting = true
      this.httpRequestStartedAt = new Date().getTime()

      const _then = data => this.onSuccessOfSubmitForm(customEventsFormatted, data)
      const _catch = error => this.onErrorOfSubmitForm(customEventsFormatted, error)
      const _finally = () => this.onCompleteOfSubmitForm(customEventsFormatted)

      axiosPromise
        .then(data => this.handleAxiosEvent(() => _then(data)))
        .catch(error => this.handleAxiosEvent(() => _catch(error)))
        .finally(() => this.handleAxiosEvent(_finally))
    },

    onSuccessOfSubmitForm(customEvents, response) {
      const successIsDefined = response.data && typeof response.data.success !== 'undefined'
      this.hasSubmitError = successIsDefined && !response.data.success
      this.submittedSuccessfully = !this.hasSubmitError

      if (this.hasSubmitError) {
        this.handleErrorsFromServer(response.data)
      } else {
        customEvents.then(response)
      }
    },

    onErrorOfSubmitForm(customEvents, error) {
      this.hasSubmitError = true
      this.submittedSuccessfully = false
      customEvents.catch(error)
    },

    onCompleteOfSubmitForm(customEvents) {
      this.isSubmitting = this.isRedirecting
      this.fixScrollRelativeToSubmitCta()
      customEvents.finally()
    },

    handleErrorsFromServer(response) {
      this.highlightErrorsFromServer(this, response)
      this.hasSubmitError = !this.errorCount
    },

    highlightErrorsFromServer(instance, response) {
      instance.errorCount = 0
      instance.hasChangeHandlers = false

      instance.fieldsArray.forEach(field => {
        const errorArray = response.errors[field.key]
        if (errorArray) {
          response.showErrorMessagesFromServer
            ? errorArray.forEach(error => field.errors.push(error))
            : field.errors.push(this.$t('errors.invalid_field'))
          instance.errorCount++
          instance.hasChangeHandlers = true
        }
      })
      instance.errorMessage = response.errors.global
      if (instance.syncFormDataOnNextTick) instance.syncFormDataOnNextTick()

      return instance.errorCount
    },

    getPermittedObject(permits, formData = {}) {
      const obj = {}
      permits.forEach(key => {
        if (typeof formData[key] != 'undefined') obj[key] = formData[key]
      })
      return obj
    },

    fixScrollRelativeToSubmitCta(noScrolling) {
      const submitCta = this.$refs.submitCta
      if (!submitCta) return

      this.relativeScrollTop = submitCta.$refs.submit.getBoundingClientRect().top

      this.$nextTick(function () {
        const scrollDiff =
          submitCta.$refs.submit.getBoundingClientRect().top - this.relativeScrollTop
        window.scrollTo(0, window.pageYOffset + scrollDiff)

        if (!noScrolling) {
          this.scrollToShowSubmitMessage()
        }
      })
    },

    scrollToShowSubmitMessage() {
      const submitCta = this.$refs.submitCta
      const msgBlock = submitCta.$refs.submitMessage.$refs.wrapper
      const msgBlockOffset = msgBlock.getBoundingClientRect().bottom
      const diff = msgBlockOffset - window.innerHeight
      const callback = () => this.flashSubmitMessage()

      if (msgBlock.offsetHeight > 0 && diff > 0) {
        const scrollPosition = window.pageYOffset + diff + 25
        On.smoothScrollTo(scrollPosition, { duration: 500, callback })
      } else {
        callback()
      }
    },

    flashSubmitMessage() {
      const submitCta = this.$refs.submitCta
      const msgBlock = submitCta.$refs.submitMessage.$refs.wrapper
      const visibleClass = 'form-submit--visible'
      clearTimeout(msgBlock.flashTimeout)
      msgBlock.classList.remove(visibleClass)

      msgBlock.flashTimeout = setTimeout(() => {
        msgBlock.classList.add(visibleClass)
      }, On.TransitionTimes.fast)
    },
  },
}
